/**
 * TimeseriesChart — lightweight time-series chart powered by uPlot.
 *
 * Renders one or more series from PromQL range query results.
 * No external dependencies beyond uPlot (~40 KB).
 */
import { useEffect, useRef } from 'react'
import uPlot from 'uplot'
import 'uplot/dist/uPlot.min.css'
import type { PromRangeResult } from '@/types'

interface TimeseriesChartProps {
  results: PromRangeResult[]
  unit?: string       // 'percent' | 'bytes' | 'Bps' | 'short'
  height?: number
  timeFrom?: number   // unix seconds
  timeTo?: number     // unix seconds
}

// ── Unit formatters ────────────────────────────────────────────────────────

function formatValue(unit: string) {
  return (_self: uPlot, rawVal: number): string => {
    if (rawVal == null || isNaN(rawVal)) return '--'
    switch (unit) {
      case 'percent': return rawVal.toFixed(1) + '%'
      case 'bytes':   return formatBytes(rawVal)
      case 'Bps':     return formatBytes(rawVal) + '/s'
      default:        return rawVal.toFixed(2)
    }
  }
}

function formatBytes(b: number): string {
  if (b >= 1_073_741_824) return (b / 1_073_741_824).toFixed(1) + ' GB'
  if (b >= 1_048_576)     return (b / 1_048_576).toFixed(1) + ' MB'
  if (b >= 1_024)         return (b / 1_024).toFixed(1) + ' KB'
  return b.toFixed(0) + ' B'
}

// ── Series colours ─────────────────────────────────────────────────────────

const COLOURS = ['#4c9ffe', '#f7c948', '#54c87a', '#e06c75', '#c678dd', '#56b6c2']

// ── Component ──────────────────────────────────────────────────────────────

export default function TimeseriesChart({
  results,
  unit = 'short',
  height = 200,
}: TimeseriesChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const plotRef      = useRef<uPlot | null>(null)

  useEffect(() => {
    if (!containerRef.current || results.length === 0) return

    // Collect all unique timestamps (sorted)
    const tsSet = new Set<number>()
    for (const r of results) {
      for (const [ts] of r.values) tsSet.add(ts)
    }
    const times = Array.from(tsSet).sort((a, b) => a - b)

    // Build uPlot data: [timestamps, ...series]
    const data: uPlot.AlignedData = [new Float64Array(times)]
    for (const r of results) {
      const map = new Map(r.values.map(([ts, v]) => [ts, parseFloat(v)]))
      const vals = times.map(ts => map.get(ts) ?? NaN)
      data.push(new Float64Array(vals))
    }

    // Build series config
    const series: uPlot.Series[] = [{}]  // first = x axis
    for (let i = 0; i < results.length; i++) {
      const metric = results[i].metric
      const label = Object.entries(metric)
        .filter(([k]) => k !== '__name__')
        .map(([k, v]) => `${k}=${v}`)
        .join(', ') || (metric['__name__'] ?? `series ${i + 1}`)
      series.push({
        label,
        stroke: COLOURS[i % COLOURS.length],
        width: 1.5,
        fill: COLOURS[i % COLOURS.length] + '18',
        value: formatValue(unit),
      })
    }

    const width = containerRef.current.clientWidth || 600

    const opts: uPlot.Options = {
      width,
      height,
      series,
      axes: [
        {
          stroke: 'var(--color-text-muted)',
          grid: { stroke: 'var(--color-border)', width: 0.5 },
          ticks: { stroke: 'var(--color-border)' },
        },
        {
          stroke: 'var(--color-text-muted)',
          grid: { stroke: 'var(--color-border)', width: 0.5 },
          ticks: { stroke: 'var(--color-border)' },
          values: (_self, vals) =>
            vals.map(v => v == null ? '' : formatValue(unit)(_self as uPlot, v)),
          size: 64,
        },
      ],
      cursor: { show: true, drag: { x: true, y: false } },
      legend: { show: results.length > 1 },
    }

    // Destroy previous instance
    if (plotRef.current) {
      plotRef.current.destroy()
      plotRef.current = null
    }

    plotRef.current = new uPlot(opts, data, containerRef.current)

    return () => {
      plotRef.current?.destroy()
      plotRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, unit, height])

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width
      if (w && plotRef.current) plotRef.current.setSize({ width: w, height })
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [height])

  if (results.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
        Nessun dato
      </div>
    )
  }

  return <div ref={containerRef} />
}
